import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpHandlerFn, HttpEvent } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError, BehaviorSubject, Subject, filter, take, race, Observable } from 'rxjs';
import { AuthService } from '../../services/auth/auth.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Skip auth for login, register, and refresh endpoints
  if (req.url.includes('/auth/login') ||
    req.url.includes('/auth/register') ||
    req.url.includes('/auth/refresh') ||
    req.url.includes('/auth/google')) {
    return next(req);
  }

  // Add Authorization header if token exists
  const token = authService.getAccessToken();
  const tokenType = authService.getTokenType();

  if (token && tokenType) {
    req = req.clone({
      setHeaders: {
        Authorization: `${tokenType} ${token}`
      }
    });
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // If 401 error and we have a refresh token, try to refresh
      if (error.status === 401 && authService.getRefreshToken()) {
        console.log('[AuthInterceptor] 401 error, attempting token refresh for:', req.url);
        return handle401Error(req, next, authService, router);
      }

      return throwError(() => error);
    })
  );
};

// Track if a token refresh is in progress
let isRefreshing = false;
let refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
// Notifies requests waiting on a refresh that it failed, so they error out instead of hanging forever.
let refreshFailedSubject: Subject<unknown> = new Subject<unknown>();

function handle401Error(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router
): Observable<HttpEvent<unknown>> {
  if (!isRefreshing) {
    console.log('[AuthInterceptor] Starting token refresh...');
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap((response: any) => {
        console.log('[AuthInterceptor] Token refreshed successfully, retrying request:', req.url);
        isRefreshing = false;
        refreshTokenSubject.next(response.access_token);

        // Retry the failed request with the new token
        // Add x-skip-loader header to prevent loading interceptor from triggering again
        const tokenType = authService.getTokenType();
        let headers = req.headers
          .set('Authorization', `${tokenType} ${response.access_token}`)
          .set('x-skip-loader', 'true');

        const clonedReq = req.clone({ headers });

        console.log('[AuthInterceptor] Cloned request headers:', clonedReq.headers.get('x-skip-loader'));

        return next(clonedReq);
      }),
      catchError((error) => {
        console.error('[AuthInterceptor] Token refresh failed:', error);
        isRefreshing = false;
        authService.logout();
        router.navigate(['/login']);
        // Release anyone waiting on this refresh — otherwise their request hangs
        // forever (refreshTokenSubject never emits a non-null token again).
        refreshFailedSubject.next(error);
        return throwError(() => error);
      })
    );
  } else {
    console.log('[AuthInterceptor] Token refresh already in progress, waiting...');
    // If refresh is already in progress, wait for it to complete — but also race
    // against a failure notification, so a failed refresh doesn't leave this
    // request pending forever.
    return race(
      refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap(token => {
          console.log('[AuthInterceptor] Token refresh completed, retrying request:', req.url);
          const tokenType = authService.getTokenType();
          let headers = req.headers
            .set('Authorization', `${tokenType} ${token}`)
            .set('x-skip-loader', 'true');

          const clonedReq = req.clone({ headers });
          return next(clonedReq);
        })
      ),
      refreshFailedSubject.pipe(
        take(1),
        switchMap(error => throwError(() => error))
      )
    );
  }
}
